import { ReactNode } from 'react';
import { Button, Select, SelectItem } from '@heroui/react';
import { Svg } from '@/shared/ui/Svg';
import DownloadIcon from '@/shared/assets/icons/download.svg';
import styles from '../../dashboard/Dashboard.module.scss';

export type AnalyticsPeriodOption = { key: string; label: string };

interface AnalyticsSectionHeaderProps {
  title: string;
  icon?: ReactNode;
  iconBg?: string;
  onExport?: () => void;
  showToolbar?: boolean;
  periodOptions?: AnalyticsPeriodOption[];
  periodValue?: string;
  onPeriodChange?: (key: string) => void;
}

const DEFAULT_PERIOD_OPTIONS: AnalyticsPeriodOption[] = [
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
];

const SELECT_ITEM_CLASSES = {
  base: [
    'rounded-lg',
    'data-[hover=true]:bg-default-100',
    'data-[selectable=true]:focus:bg-default-100',
    'data-[selected=true]:bg-default-100',
    'data-[selected=true]:text-black',
  ].join(' '),
  title: '!text-black data-[selected=true]:!text-black',
  selectedIcon: '!text-black [&_path]:!stroke-black [&_path]:![fill:none]',
};

export const AnalyticsSectionHeader = ({
  title,
  icon,
  iconBg = '#E8F0FF',
  onExport,
  showToolbar = true,
  periodOptions = DEFAULT_PERIOD_OPTIONS,
  periodValue,
  onPeriodChange,
}: AnalyticsSectionHeaderProps) => {
  const selectedKey = periodValue ?? periodOptions[0]?.key;

  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionTitleRow}>
        {icon ? (
          <div className={styles.sectionIcon} style={{ backgroundColor: iconBg }}>
            {icon}
          </div>
        ) : null}
        <h2 className={styles.sectionTitle}>{title}</h2>
      </div>
      {showToolbar ? (
        <div className={styles.toolbar}>
          <Select
            aria-label="Period"
            color="default"
            selectedKeys={selectedKey ? new Set([selectedKey]) : new Set()}
            onSelectionChange={(keys) => {
              if (!onPeriodChange || keys === 'all') return;
              const key = Array.from(keys)[0];
              if (key !== undefined) onPeriodChange(String(key));
            }}
            classNames={{
              base: styles.toolbarSelectBase,
              trigger: styles.toolbarSelectTrigger,
              value: styles.toolbarSelectValue,
              listbox: styles.toolbarSelectListbox,
              popoverContent: styles.toolbarSelectPopover,
            }}
            popoverProps={{
              shouldBlockScroll: false,
              classNames: {
                content: `min-w-[160px] dashboard-period-select ${styles.toolbarSelectPopover}`,
              },
            }}
          >
            {periodOptions.map((option) => (
              <SelectItem key={option.key} classNames={SELECT_ITEM_CLASSES}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
          <Button
            isIconOnly
            aria-label="Download"
            className={`${styles.toolbarButton} bg-[#211D55] text-white`}
            radius="sm"
            onPress={onExport}
          >
            <Svg Icon={DownloadIcon} width={20} height={20} />
          </Button>
        </div>
      ) : null}
    </div>
  );
};
